import { type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes, useId } from 'react';

/**
 * Campos editáveis no visual do design system (rótulo eyebrow + caixa com borda `border` e raio
 * `radius-control`). O DS não tem input editável; estes seguem as medidas do campo do RouteSearch.
 */
interface FieldFrame {
  label: string;
  error?: string | undefined;
  hint?: ReactNode;
}

function Frame({ id, label, error, hint, children }: FieldFrame & { id: string; children: ReactNode }) {
  return (
    <div className="app-field">
      <label htmlFor={id} className="eyebrow vc-eyebrow">
        {label}
      </label>
      <div className="app-field-box" data-invalid={error ? 'true' : undefined}>
        {children}
      </div>
      {error ? (
        <span id={`${id}-err`} className="caption app-field-error" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span id={`${id}-hint`} className="caption app-field-hint">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

const describedBy = (id: string, f: FieldFrame) =>
  f.error ? `${id}-err` : f.hint ? `${id}-hint` : undefined;

export function TextField({
  label,
  error,
  hint,
  prefix,
  ...input
}: InputHTMLAttributes<HTMLInputElement> & FieldFrame & { prefix?: string }) {
  const id = useId();
  return (
    <Frame id={id} label={label} error={error} hint={hint}>
      {prefix ? (
        <span className="app-field-prefix" aria-hidden="true">
          {prefix}
        </span>
      ) : null}
      <input
        id={id}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy(id, { label, error, hint })}
        {...input}
      />
    </Frame>
  );
}

export function TextAreaField({
  label,
  error,
  hint,
  ...area
}: TextareaHTMLAttributes<HTMLTextAreaElement> & FieldFrame) {
  const id = useId();
  return (
    <Frame id={id} label={label} error={error} hint={hint}>
      <textarea
        id={id}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy(id, { label, error, hint })}
        {...area}
      />
    </Frame>
  );
}
